import * as React from "react"
import { useEffect, useState } from "react"
import { useTheme } from "@emotion/react"
import { useHistory } from "react-router"
import {
    Box,
    Button,
    CharacterCounter,
    Heading,
    TextAreaFormField,
    Text,
    Alert,
    AlertTitle,
    VisuallyHidden,
    Flex,
} from "@indeed/ifl-components"
import type { IndeedTheme } from "@indeed/ifl-themes"
import { StickyBox } from "../../../common/components/sticky-box"
import { useQueryParams } from "../../../common/hooks/hooks"
import { useLocale } from "../../../common/hooks/useLocale"
import { configSelector } from "../../../common/utils/config-slice"
import { useI18n } from "../../../common/utils/i18n-context"
import { jobInfoSelector } from "../../../common/utils/job-info"
import { useAppDispatch, useAppSelector } from "../../../common/utils/redux-helpers"
import { alertThrowAndLogDatadogRum, ifTrue, parseEssentialQueryParams, Preconditions, ReqStatus } from "../../../common/utils/utils"
import {
    DislikePreferencesSelector,
    DislikePreferencesHeader,
    logDisplayedPreferencesBeforeSubmit,
    submitExplicitFeedback,
    actions,
    optsSelector,
    requirementsSelector,
    toggledRequirementsSelector,
} from "../../feedback-form"
import { handleStickyBoxOverlap } from "../../feedback-form/helpers"
import { NegativeQualificationsSelector } from "../../feedback-form/negative-qualifications-selector/negative-qualifications-selector"
import { PauseEntry } from "./pause-entry"

export const ExplicitFeedbackPage = ({ stickySubmitButton = true }) => {
    const history = useHistory()
    const encryptedInfo = parseEssentialQueryParams(useQueryParams()).info
    const [feedbackText, setFeedbackText] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [formInvalid, setFormInvalid] = useState(false)
    const dispatch = useAppDispatch()
    const config = useAppSelector(configSelector)
    const job = useAppSelector(jobInfoSelector)
    const { totalNumberOfRequirements } = useAppSelector(requirementsSelector)
    const [wasSubmitButtonClicked, setWasSubmitButtonClicked] = useState(false)
    const useTextLabelForCategory = config.proctorGroups.jsj_mrp_jcr_feedback_design_tst === 1
    const showNewContentStrings = config.proctorGroups.jsj_mrp_jcr_content_strings_tst === 1
    const showNegativeQualifications =
        config.proctorGroups.jsj_mrp_jcr_negative_qualifications_feedback_page_tst === 1 && totalNumberOfRequirements !== 0
    const theme = useTheme() as IndeedTheme
    const { gettext } = useI18n()
    const locale = useLocale()

    useEffect(() => {
        dispatch(actions.generateAndSetOpts({ job, locale, gettext }))
        dispatch(actions.generateAndSetJobRequirements({ job, gettext }))
        dispatch(logDisplayedPreferencesBeforeSubmit({ info: encryptedInfo }))
    }, [])

    const submitForm = async () => {
        try {
            setIsSubmitting(true)
            const res = await dispatch(
                submitExplicitFeedback({
                    explicitFeedbackRequest: {
                        encryptedInfo,
                        feedbackText,
                    },
                })
            ).unwrap()
            setIsSubmitting(false)
            if (res === ReqStatus.LIMIT_REACHED) {
                history.push("/invitations/feedback/limit")
                return
            }
            Preconditions.checkState(res === ReqStatus.SUCCESS, "submission not ok")
            history.push(config.feedbackThanksUrl)
        } catch {
            setIsSubmitting(false)
            alertThrowAndLogDatadogRum(gettext("Your changes were not saved. Try again."), gettext("Something's not right"))
        }
    }

    const handleSaveFeedbackClick = () => {
        if (!wasSubmitButtonClicked) {
            setWasSubmitButtonClicked(true)
        }

        if (isSubmitDisabled) return

        submitForm()
    }

    const opts = useAppSelector(optsSelector)
    const hasFeedbackCategories = opts.length !== 0
    const hasToggledRequirements = useAppSelector(toggledRequirementsSelector).length !== 0

    const isSubmitDisabled = !(opts.some((it) => it.selected) || feedbackText.trim() !== "" || hasToggledRequirements) || formInvalid

    const getVisuallyHiddenText = () => {
        if (!wasSubmitButtonClicked) return ""

        return isSubmitDisabled ? gettext("Please select at least one option") : gettext("Please hit the save button to send your feedback")
    }

    return (
        <Box
            sx={{
                maxInlineSize: "35.625rem",
                marginBlockEnd: 0,
                marginBlockStart: 0,
                marginInlineEnd: "auto",
                marginInlineStart: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                inlineSize: "100%",
            }}
            id="explicit-feedback-page-main"
        >
            {ifTrue(hasFeedbackCategories || showNegativeQualifications, () => {
                return (
                    <Box
                        sx={{
                            paddingBlockEnd: 4,
                            paddingBlockStart: 4,
                            paddingInlineEnd: 4,
                            paddingInlineStart: 4,
                        }}
                    >
                        <Heading as="h1" level={[4, 5]} sx={{ marginBlockEnd: `${theme.space[5]} !important`, marginBlockStart: 2 }}>
                            {showNewContentStrings
                                ? gettext("Hide the job details that aren't a match")
                                : gettext("Tell us why this job isn't a good fit")}
                        </Heading>
                        <DislikePreferencesHeader
                            headerText={
                                showNewContentStrings
                                    ? gettext("We’ll learn from your choices and show fewer jobs like this in the future.")
                                    : gettext("We'll show you fewer jobs across Indeed with these details. Select all that apply.")
                            }
                        />
                    </Box>
                )
            })}
            {ifTrue(showNegativeQualifications, () => (
                <Box
                    sx={{
                        inlineSize: "100%",
                        display: "flex",
                        flexDirection: "column",
                        paddingBlockEnd: hasFeedbackCategories ? 4 : 0,
                    }}
                    id="explicit-feedback-page-job-qualifications"
                >
                    <Heading
                        level={[2, 3]}
                        sx={{
                            borderBlockEnd: "1px solid",
                            borderBlockEndColor: "border.weak",
                            marginBlockEnd: 0,
                            marginBlockStart: 0,
                            marginInlineEnd: 0,
                            marginInlineStart: 0,
                            paddingBlockEnd: 4,
                            paddingBlockStart: 4,
                            paddingInlineEnd: 4,
                            paddingInlineStart: 4,
                            ...(useTextLabelForCategory
                                ? {
                                      marginInlineEnd: 4,
                                      marginInlineStart: 4,
                                      paddingInlineEnd: 0,
                                      paddingInlineStart: 0,
                                  }
                                : {}),
                        }}
                    >
                        {gettext("Job qualifications")}
                    </Heading>
                    <NegativeQualificationsSelector />
                </Box>
            ))}
            <Box
                sx={{
                    inlineSize: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
                id="explicit-feedback-page-job-details"
            >
                {ifTrue(hasFeedbackCategories, () => {
                    return (
                        <Heading
                            level={[2, 3]}
                            sx={{
                                borderBlockEnd: "1px solid",
                                borderBlockEndColor: "border.weak",
                                marginBlockEnd: 0,
                                marginBlockStart: 0,
                                marginInlineEnd: 0,
                                marginInlineStart: 0,
                                paddingBlockEnd: 4,
                                paddingBlockStart: 4,
                                paddingInlineEnd: 4,
                                paddingInlineStart: 4,
                                ...(useTextLabelForCategory
                                    ? {
                                          marginInlineEnd: 4,
                                          marginInlineStart: 4,
                                          paddingInlineEnd: 0,
                                          paddingInlineStart: 0,
                                      }
                                    : {}),
                            }}
                        >
                            {gettext("Job details")}
                        </Heading>
                    )
                })}
                <DislikePreferencesSelector />
            </Box>
            <Box
                sx={{
                    inlineSize: "100%",
                    paddingBlockEnd: 4,
                    paddingBlockStart: 4,
                    paddingInlineEnd: 4,
                    paddingInlineStart: 4,
                    marginBlockEnd: 8,
                }}
                data-testid="additional-details-wrapper"
                onFocus={handleStickyBoxOverlap}
            >
                <TextAreaFormField
                    invalid={formInvalid}
                    label={
                        <Flex sx={{ marginBlockEnd: 2 }}>
                            <Text level={3} variant="contrast" sx={{ fontWeight: "bold" }}>
                                {gettext("Additional details")}
                            </Text>
                            <Text as="span" sx={{ fontWeight: "normal", whiteSpace: "pre" }} level={3}>
                                {" "}
                                ({gettext("optional")})
                            </Text>
                        </Flex>
                    }
                    helperText={gettext("This feedback won't remove jobs but may be used to make improvements across Indeed.")}
                    counter={
                        <CharacterCounter
                            data-testid="open-feedback-counter"
                            value={feedbackText.length}
                            limit={config.maxFeedbackTextLength}
                            formatter={(currentValue, limit) => `${gettext("Character count:")} ${currentValue} / ${limit}`}
                        />
                    }
                    textareaProps={{
                        value: feedbackText,
                        onChange: (ev) => {
                            setFormInvalid(ev.target.value.length > config.maxFeedbackTextLength)
                            setFeedbackText(ev.target.value)
                        },
                    }}
                />
                <Alert status="info" sx={{ marginBlockStart: 2 }}>
                    <AlertTitle as="h2">{gettext("Protect your privacy")}</AlertTitle>
                    {gettext("Avoid disclosing personal information like your name and phone number")}
                </Alert>
            </Box>
            <StickyBox
                sx={{
                    inlineSize: "100%",
                    paddingBlockEnd: 5,
                    paddingBlockStart: 5,
                    paddingInlineEnd: 5,
                    paddingInlineStart: 5,
                    ...(stickySubmitButton ? {} : { position: "static" }),
                }}
                blockSize="9.5rem"
                id="explicit-fbk-page-sticky-box"
            >
                <Button
                    full
                    id="explicit-fbk-page-submit-btn"
                    loading={isSubmitting}
                    variant="primary"
                    aria-disabled={isSubmitDisabled}
                    sx={{ maxInlineSize: "35.625rem", marginInlineStart: "auto", marginInlineEnd: "auto" }}
                    onClick={handleSaveFeedbackClick}
                    type="button"
                >
                    {gettext("Save")}
                </Button>
                <VisuallyHidden aria-live="polite">{getVisuallyHiddenText()}</VisuallyHidden>
                <PauseEntry />
            </StickyBox>
        </Box>
    )
}
